export class PlatformUtil {
  /**
   * Check if the current platform is MS Edge.
   *
   * @return {boolean}
   */
  static isEdge(): boolean {
    // Legacy Edge contains "Edge/version".
    // Chromium-based Edge contains "Edg/version" (no "e").
    return !!navigator.userAgent.match(/Edge?\//);
  }

  /**
   * Check if the current platform is Legacy Edge.
   *
   * @return {boolean}
   */
  static isLegacyEdge(): boolean {
    // Legacy Edge contains "Edge/version".
    // Chromium-based Edge contains "Edg/version" (no "e").
    return !!navigator.userAgent.match(/Edge\//);
  }

  /**
   * Check if the current platform is MS IE.
   *
   * @return {boolean}
   */
  static isIE(): boolean {
    return PlatformUtil.userAgentContains_('Trident/');
  }

  /**
   * Check if the current platform is an Xbox.
   *
   * @return {boolean}
   */
  static isXbox(): boolean {
    return PlatformUtil.userAgentContains_('Xbox');
  }

  /**
   * Check if the current platform is a Tizen TV.
   *
   * @return {boolean}
   */
  static isTizen(): boolean {
    return PlatformUtil.userAgentContains_('Tizen');
  }

  /**
   * Check if the current platform is a WebOS.
   *
   * @return {boolean}
   */
  static isWebOS(): boolean {
    return PlatformUtil.userAgentContains_('Web0S');
  }

  /**
   * Check if the current platform is Comcast X1.
   *
   * @return {boolean}
   */
  static isWPE(): boolean {
    return PlatformUtil.userAgentContains_('WPE');
  }

  /**
   * Check if the current platform is PlayStation 4.
   *
   * @return {boolean}
   */
  static isPS4(): boolean {
    return PlatformUtil.userAgentContains_('PlayStation 4');
  }

  /**
   * Check if the current platform is PlayStation 5.
   *
   * @return {boolean}
   */
  static isPS5(): boolean {
    return PlatformUtil.userAgentContains_('PlayStation 5');
  }

  /**
   * Check if the current platform is from Apple.
   *
   * Returns true on all iOS browsers and on desktop Safari.
   *
   * Returns false for non-Safari browsers on macOS, which are independent of
   * Apple.
   *
   * @return {boolean}
   */
  static isApple(): boolean {
    return !!navigator.vendor && navigator.vendor.includes('Apple') && !PlatformUtil.isTizen() && !PlatformUtil.isWPE();
  }

  /**
   * Check if the current platform is Google Chrome.
   *
   * @return {boolean}
   */
  static isChrome(): boolean {
    // The Edge user agent will also contain the "Chrome" keyword, so we need
    // to make sure this is not Edge.
    return PlatformUtil.userAgentContains_('Chrome') && !this.isEdge();
  }

  /**
   * Check if the current platform is a Google Chromecast.
   *
   * @return {boolean}
   */
  static isChromecast(): boolean {
    return PlatformUtil.userAgentContains_('CrKey');
  }

  /**
   * check if the current platform is a VIZIO TV.
   *
   * 5.0 (X11; Linux armv7l) AppleWebKit/537.36 (KHTML, like Gecko)
   * Chrome/72.0.3626.121 Safari/537.36 VIZIO SmartCast(Conjure/0.0.5016 FW/4.0.1.1
   * Model/E43-F1)
   *
   * @returns {boolean}
   */

  static isVizio(): boolean {
    return PlatformUtil.userAgentContains_('VIZIO');
  }

  /**
   * Check if the current platform is VIDAA OS.
   * @return {boolean}
   */
  static isVIDAA(): boolean {
    return PlatformUtil.userAgentContains_('VIDAA');
  }

  /**
   * Check if the current platform is a Viera TV.
   * @return {boolean}
   */
  static isPanasonicTV(): boolean {
    return PlatformUtil.userAgentContains_('VIERA');
  }

  /**
   * Detect if the current device is a mobile or tablet using userAgentContains_.
   *
   * @return {boolean}
   */
  static isMobile(): boolean {
    const matchList = ['Android', 'iPhone', 'iPad', 'iPod', 'Mobile', 'Tablet'];

    return matchList.some(deviceType => PlatformUtil.userAgentContains_(deviceType));
  }

  /**
   * Check if the current platform is desktop (non-mobile, non-TV).
   *
   * @return {boolean}
   */
  static isDesktop(): boolean {
    return !this.isMobile() && !this.isSmartTV();
  }

  /**
   * Return true if the platform is supported single media source
   *
   * @return {boolean}
   */
  static isSmartTV(): boolean {
    return Boolean(
      this.isTizen() ||
      this.isWebOS() ||
      this.isXbox() ||
      this.isPS4() ||
      this.isPS5() ||
      this.isVizio() ||
      this.isVIDAA() ||
      this.isPanasonicTV() ||
      this.isWPE() ||
      this.isChromecast(),
    );
  }

  /**
   * Check if the user agent contains a key. This is the best way we know of
   * right now to detect platforms.
   *
   * @param {string} key
   * @return {boolean}
   * @private
   */
  static userAgentContains_(key: string): boolean {
    const userAgent = navigator.userAgent || '';
    return userAgent.includes(key);
  }
}
